import React, { Component, ComponentType } from 'react'
import { Pagination, PaginationProps, styled, TablePagination, TablePaginationProps, useMediaQuery, useTheme } from '@mui/material'
import { createFilterStore } from './filter-store'
import { IFilterState, IPagination } from './types'

export interface IPaginationBarConfigs {
  rowsPerPageOptions?: number[]
  disableScrollLock?: boolean
  enablePaginationTop?: boolean
}

export interface IPaginationBarProps<T> extends IPaginationBarConfigs {
  count?: number
  filter?: IFilterState<T>
  defaultFilter?: IFilterState<T>
  onChange?: (value: IFilterState<T>) => void
  isMobile?: boolean
}

export type IPaginationBarParams<T> = Pick<IPaginationBarProps<T>, 'defaultFilter' | 'rowsPerPageOptions'>

export function createTablePagination<T>(): ComponentType<IPaginationBarProps<T>> {
  class PaginationBar extends Component<IPaginationBarProps<T>> {
    get defaultFilter(): IFilterState<T> {
      return this.props.defaultFilter || {}
    }

    get defaultPagination(): Required<IPagination> {
      return (this.defaultFilter?.pagination as any) || { page: 0, pageSize: 10 }
    }

    get pagination(): Required<IPagination> {
      return (this.props.filter?.pagination as any) || this.defaultPagination
    }

    get rowsPerPageOptions(): number[] {
      return this.props.rowsPerPageOptions || [10, 25, 50, 100]
    }

    handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      const store = createFilterStore<T>(this.props.filter ?? {}, this.defaultFilter)
      const { pageSize } = this.pagination
      const obj: IPagination = { page: newPage, pageSize }
      store.changePagination(obj, { reason: 'pagination' })
      this.props.onChange?.(store.build())
    }

    handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newPageSize = parseInt(event.target.value, 10)
      const store = createFilterStore<T>(this.props.filter ?? {}, this.defaultFilter)
      store.changePagination({ page: 0, pageSize: newPageSize }, { reason: 'pagination' })
      this.props.onChange?.(store.build())
    }

    render() {
      const { page, pageSize } = this.pagination
      const { isMobile } = this.props
      if (!this.props.count || (Math.ceil(this.props.count / pageSize) === 1 && isMobile)) {
        return null // No pagination if there are no items
      }
      if (isMobile) {
        return (
          <PaginationCustom
            siblingCount={0}
            page={page + 1}
            count={Math.ceil(this.props.count / pageSize)}
            onChange={(_, value) => this.handleChangePage(null, value - 1)}
            size='small'
            color='primary'
            sx={{ my: 1 }}
          />
        )
      }
      return (
        <TablePaginationCustom
          size='small'
          page={page}
          count={this.props.count || 0}
          onPageChange={this.handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          rowsPerPageOptions={this.rowsPerPageOptions}
          slotProps={{
            actions: { nextButton: { size: 'small' }, previousButton: { size: 'small' } },
            select: { MenuProps: { disableScrollLock: this.props.disableScrollLock } }
          }}
        />
      )
    }
  }
  return PaginationBar
}

export function createPaginationBar<T>(params?: IPaginationBarParams<T>) {
  const PaginationBar = createTablePagination<T>()
  return function ResponsivePaginationBar(props: IPaginationBarProps<T>) {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    return <PaginationBar {...props} isMobile={isMobile} />
  }
}

export default createPaginationBar

const TablePaginationCustom = styled((p: TablePaginationProps) => <TablePagination component='div' {...p} />)(({ theme }) => ({
  marginLeft: 'auto',
  '@media (min-width: 600px)': {
    '.MuiToolbar-root': {
      minHeight: '48px'
    }
  },
  '.MuiTablePagination-selectLabel': {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  }
}))

const PaginationCustom = styled((p: PaginationProps) => <Pagination {...p} />)({
  marginLeft: 'auto',
  '.Mui-selected': {
    backgroundColor: 'color-mix(in srgb, rgb(232, 140, 63) 60%, transparent) !important'
  },
  '@media (min-width: 600px)': {
    '.MuiToolbar-root': {
      minHeight: '48px'
    }
  }
})
